import {
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
} from "@mui/material"
import { useEffect, useRef, useState } from "react"
import DebugService from "../services/DebugService"
import { LoadingButton } from "@mui/lab"
import {
    CONST_SITE_LABEL,
    STORAGE_KEYS,
    setScopedItem
} from "./common/Helper"
import SnackbarNotification, { SNACKBAR_THEME } from "./common/SnackbarNotification"
import ConnectionApiService from "../services/ConnectionApiService"
import { GrDeploy } from "react-icons/gr";
import './common/Styles.css'

import AnsiToHtml from "ansi-to-html";
import { useAuth } from "../state/AuthContext"
import { useScopedLocalStorage } from "../hooks/useScopedLocalStorage"

const ansiConverter = new AnsiToHtml();

/**
 * Manage Service component
 * @returns 
 */
export const ManageService = () => {
    const [data, setData] = useState('')
    const [connectionList, setConnectionList] = useState([])
    const [serviceList, setServiceList] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState(null)
    const [snackbarStatus, setSnackbarStatus] = useState(null)
    const [currentUserAccess, setCurrentUserAccess] = useState(null)
    const [serviceForm, setServiceForm] = useState({
        service: '',
        server: '',
    })
    const [serviceLogsApiData, setServiceLogsApiData] = useState([])
    const [serviceListApiData, setServiceListApiData] = useState([])
    const [connectionListApiData, setConnectionListApiData] = useState([])

    const logContainerRef = useRef(null);
    const { selectedAccount } = useAuth()
    let fetchLogsInterval = useRef(null)
    const { getScopedItem } = useScopedLocalStorage()

    const defaultLogsText = 'Fetching Logs <span class="dots-animation"></span>'

    /**
     * Fetch connection list
     */
    const fetchConnectList = () => {
        setIsLoading(true)

        ConnectionApiService.getData(selectedAccount)
            .then(res => {
                DebugService.log(`Manage Service Component: API response`, res)
                setConnectionListApiData(res);
                const apiData = (res['data'] ?? []).filter(c => c['isActive'] === 1)
                setConnectionList(apiData)

                if (res['error']) {
                    DebugService.error(`Manage Service Component: Error caught while fetching data`, res['error'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`Manage Service Component: Error caught while fetching data`, err)
                setIsLoading(false)
            })
    }

    /**
     * Fetch service list
     */
    const fetchServiceList = () => {
        setIsLoading(true)

        ConnectionApiService.getServiceList(selectedAccount)
            .then(res => {
                DebugService.log(`Manage Service Component: Service List API response`, res)
                setServiceListApiData(res)
                const apiData = res['data'] ?? []
                setServiceList(apiData)

                if (res['error']) {
                    DebugService.error(`Manage Service Component: Error caught while fetching service list`, res['error'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`Manage Service Component: Error caught while fetching service list`, err)
                setIsLoading(false)
            })
    }

    /**
     * Fetch logs for deployed service
     */
    const fetchLogs = (jobID) => {
        setIsLoading(true)

        ConnectionApiService.getDeployServiceLogs(selectedAccount, jobID)
            .then(res => {
                DebugService.log(`Manage Service Component: API response for job id ${jobID}`, res)
                setServiceLogsApiData(res)
                const apiData = res['data'] ?? ''

                if (apiData['log'] && apiData['log']['content'] && apiData['log']['content'] !== '') {
                    setData(ansiConverter.toHtml(apiData['log']['content'].replace(/\n/g, "<br>")))
                }

                if (res['error']) {
                    DebugService.error(`Manage Service Component: Error caught while fetching data`, res['error'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`Manage Service Component: Error caught while fetching data`, err)
                setIsLoading(false)
            })
    }

    /**
     * Call useEffect on page load
     */
    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | Manage Service`

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)
        setCurrentUserAccess(parseToJson['roleName'])
        fetchConnectList()
        fetchServiceList()

        // const interval = setInterval(() => {
        //   const newLog = `Log [${new Date().toLocaleTimeString()}]: ${
        //     Math.random() > 0.5 ? "Operation Successful ✅" : "Error Occurred ❌"
        //   }`;
        //   setLogs((prevLogs) => [...prevLogs, newLog]);

        //   // Auto-scroll to bottom
        //   setTimeout(() => {
        //     if (logContainerRef.current) {
        //       logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        //     }
        //   }, 100);
        // }, 2000);

        // return () => clearInterval(fetchLogsInterval);
    }, []);

    useEffect(() => {
        fetchConnectList()
        fetchServiceList()
    }, [selectedAccount])

    useEffect(() => {
        if (data !== '' && data !== 'FETCHING_LOGS' && fetchLogsInterval.current) {
            clearInterval(fetchLogsInterval.current)
            setServiceForm({
                service: '',
                server: '',
            })
            fetchLogsInterval.current = null
        }
    }, [data]);

    /**
    * @description Stores the latest API response in storage.
    * @author Ashish Sharma
    */
    useEffect(() => {
        setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA,{
            serviceLogsApiData: serviceLogsApiData, 
            serviceListApiData: serviceListApiData, 
            connectionListApiData: connectionListApiData
        })
    },[serviceLogsApiData, serviceListApiData, connectionListApiData]);
    /**
     * API call handler
     * @param {*} endpoint 
     * @param {*} requestBody 
     */
    const saveAPICallHandler = (requestBody) => {
        setSnackbarStatus(null)
        setSnackbarMessage(null)
        DebugService.log(`Manage Service Component: Request to deploy service initiated`, requestBody)

        ConnectionApiService.deployService(requestBody)
            .then(res => {
                DebugService.log(`Manage Service Component: API response`, res)

                if (res['data']) {
                    setSnackbarStatus('success')
                    setSnackbarMessage('Deployment Initiated Successfully. Do not navigate from the page until deployment is in progress.')
                    setData('FETCHING_LOGS')
                    setTimeout(() => {
                        fetchLogs(res['data']['jobId'] ?? 0)
                    }, 1000)

                    fetchLogsInterval.current = setInterval(() => {
                        fetchLogs(res['data']['jobId'] ?? 0)
                    }, 20000);
                }

                if (res['error']) {
                    setSnackbarStatus('failed')
                    setSnackbarMessage(res['error']['message'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.log(`Manage Service Component: Failed to deploy`, err)
                setIsLoading(false)
                setSnackbarStatus('failed')
                setSnackbarMessage('Something went wrong. Please try later')
            })
    }

    /**
     * Add new connection handler
     * @param {*} formDetails 
     */
    const onSaveClickHandler = () => {
        DebugService.log(`Manage Service Component: Form Data`, serviceForm)
        setIsLoading(true)
        setSnackbarMessage(null)
        setSnackbarStatus(null)

        const requestBody = {
            service: serviceForm['service']['service'] ?? '',
            serviceName: serviceForm['service']['name'] ?? '',
            projectId: serviceForm['service']['projectId'] ?? '',
            connectionId: serviceForm['server'],
        }

        if (selectedAccount) {
            requestBody['userAccountId'] = selectedAccount
        }

        saveAPICallHandler(requestBody)
    }

    return (
        <Grid className='manage-service-page' container spacing={1} >
            <Grid item xs={12} sm={6} md={4}>
                <FormControl
                    fullWidth
                    size="small"
                    required
                // error={errorMessages && errorMessages['infraType'] !== undefined}
                >
                    <InputLabel id="demo-simple-select-label">Service</InputLabel>
                    <Select
                        labelid="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Service"
                        onChange={(e) => setServiceForm(prev => {
                            return {
                                ...prev,
                                service: e['target']['value']
                            }
                        })}
                        value={serviceForm['service']}

                    >
                        {serviceList.map((s, i) => (
                            <MenuItem key={i} value={s}>{s['name']}</MenuItem>
                        ))}
                    </Select>
                    {/* <FormHelperText>{errorMessages && errorMessages['infraType']}</FormHelperText> */}
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FormControl
                    fullWidth
                    size="small"
                    required
                // error={errorMessages && errorMessages['infraType'] !== undefined}
                >
                    <InputLabel id="demo-simple-select-label">Server</InputLabel>
                    <Select
                        labelid="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Server"
                        // multiple
                        onChange={(e) => setServiceForm(prev => {
                            return {
                                ...prev,
                                server: e['target']['value']
                            }
                        })}
                        value={serviceForm['server']}

                    >
                        {connectionList.length > 0 && connectionList.map(c => (
                            <MenuItem key={c['connectionId']} value={c['connectionId']}>{c['connectionName']} ({c['hostName']})</MenuItem>
                        ))}
                    </Select>
                    {/* <FormHelperText>{errorMessages && errorMessages['infraType']}</FormHelperText> */}
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <LoadingButton
                    fullWidth
                    className="submit-btn"
                    variant="contained"
                    title='Add New Connection'
                    onClick={() => {
                        onSaveClickHandler()
                    }}
                    loading={isLoading}
                    loadingPosition='end'
                    endIcon={<GrDeploy />}
                >
                    Deploy
                </LoadingButton>
            </Grid>

            {/* Service List */}
            <Grid item xs={12}>
                <Paper
                    elevation={3}
                    className="logs-container"
                    ref={logContainerRef}
                >
                    <pre className="log" dangerouslySetInnerHTML={{ __html: data === 'FETCHING_LOGS' ? defaultLogsText : data }} />
                </Paper>
            </Grid>

            {/* Snackbar Notification */}
            {snackbarStatus && <SnackbarNotification
                initialOpen={true}
                duration={5000}
                message={snackbarMessage}
                actionButtonName={'Ok'}
                theme={snackbarStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
                yPosition={'top'}
                xPosition={'center'}
            />}
        </Grid>
    )
}